import React from "react";
import { formatNumberByCountry } from "../../utils/formatNumbers";
import { haceCuanto } from "../../services/estadoDeLosDocumentos";

/**
 * La tercera columna del tablero de Inicio: dónde se quedaron los documentos del
 * período y cuáles necesitan a una persona.
 *
 * Todo lo que pinta viene calculado de `services/estadoDeLosDocumentos.js`; aquí
 * sólo se decide cómo se ve. Cada paso y cada fila es un grupo con nombre, para
 * que un lector de pantalla diga de qué es cada cifra.
 */

const cuenta = (valor, country) =>
  country ? formatNumberByCountry(valor || 0, country, false, 0) : String(valor ?? 0);

const porcentaje = (parte, total) => (total > 0 ? Math.round((parte / total) * 100) : 0);

const PasoDelEmbudo = ({ nombre, valor, anterior, emitidas, country, nota }) => (
  <div role="group" aria-label={nombre}>
    <div className="bar-item__top">
      <span className="bar-item__name">{nombre}</span>
      <span className="bar-item__value">
        {cuenta(valor, country)}
        {anterior != null && ` · ${porcentaje(valor, anterior)} %`}
      </span>
    </div>
    <div className="bar-track">
      <div
        className="bar-fill"
        style={{ width: `${emitidas > 0 ? (valor / emitidas) * 100 : 0}%`, "--fill": "#00828b" }}
      ></div>
    </div>
    {nota && <div className="funnel-note">{nota}</div>}
  </div>
);

const FilaDeAtencion = ({ nombre, grupo, grave, country }) => {
  if (!grupo) return null;
  const { cuantos, recientes } = grupo;
  return (
    <div className="attention-row" role="group" aria-label={nombre}>
      <div className="attention-row__top">
        <span className="attention-row__name">{nombre}</span>
        <span
          className={`attention-count ${
            cuantos === 0 ? "" : grave ? "attention-count--danger" : "attention-count--warning"
          }`}
        >
          {cuenta(cuantos, country)}
        </span>
      </div>
      {recientes.length > 0 && (
        <ul className="attention-docs">
          {recientes.map((doc) => (
            <li key={`${nombre}-${doc.numero}`}>
              <span className="attention-docs__no">{doc.numero}</span>
              {doc.motivo && <span className="attention-docs__why">{doc.motivo}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const EstadoDeLosDocumentos = ({ estado, sinEnviar, country }) => {
  if (!estado) return null;
  const { embudo, atencion } = estado;

  const pendientesDeRevisar = [
    atencion.rechazadasPorHacienda,
    atencion.noTimbraran,
    atencion.frenadas,
    atencion.esperandoReintento,
  ]
    .filter(Boolean)
    .reduce((total, grupo) => total + grupo.cuantos, 0);

  return (
    <>
      {/* Del cobro al timbrado */}
      <div className="panel">
        <div className="panel__head">
          <h3 className="panel__title">Estado de los documentos</h3>
        </div>
        <p className="panel__sub">Facturas y notas de crédito del período</p>
        {embudo.emitidas === 0 ? (
          <p className="panel__empty">Sin documentos en el período.</p>
        ) : (
          <div className="bar-list">
            <PasoDelEmbudo
              nombre="Emitidas"
              valor={embudo.emitidas}
              emitidas={embudo.emitidas}
              country={country}
            />
            <PasoDelEmbudo
              nombre="En Business Central"
              valor={embudo.enBusinessCentral}
              anterior={embudo.emitidas}
              emitidas={embudo.emitidas}
              country={country}
            />
            <PasoDelEmbudo
              nombre="Contabilizadas"
              valor={embudo.contabilizadas}
              anterior={embudo.enBusinessCentral}
              emitidas={embudo.emitidas}
              country={country}
            />
            {embudo.timbradas !== null && (
              <PasoDelEmbudo
                nombre="Timbradas"
                valor={embudo.timbradas}
                anterior={embudo.contabilizadas}
                emitidas={embudo.emitidas}
                country={country}
                nota={
                  embudo.sinConsultar > 0
                    ? `${cuenta(embudo.sinConsultar, country)} contabilizada${
                        embudo.sinConsultar === 1 ? "" : "s"
                      } sin consultar su timbre: actualiza correlativos para saberlo.`
                    : null
                }
              />
            )}
          </div>
        )}
      </div>

      {/* Necesitan atención */}
      <div className="panel">
        <div className="panel__head">
          <h3 className="panel__title">Necesitan atención</h3>
        </div>
        <p className="panel__sub">Documentos del período que no llegaron hasta el final</p>
        {pendientesDeRevisar === 0 ? (
          <p className="panel__empty">Nada que revisar en el período.</p>
        ) : (
          <div className="attention-list">
            <FilaDeAtencion
              nombre="Rechazadas por Hacienda"
              grupo={atencion.rechazadasPorHacienda}
              grave
              country={country}
            />
            <FilaDeAtencion
              nombre="No se van a timbrar"
              grupo={atencion.noTimbraran}
              grave
              country={country}
            />
            <FilaDeAtencion
              nombre="Frenadas, sin reintento"
              grupo={atencion.frenadas}
              grave
              country={country}
            />
            <FilaDeAtencion
              nombre="Esperando reintento"
              grupo={atencion.esperandoReintento}
              country={country}
            />
          </div>
        )}
      </div>

      {/* Lo más viejo sin enviar */}
      <div className="panel">
        <div className="panel__head">
          <h3 className="panel__title">Sin enviar a Business Central</h3>
        </div>
        <p className="panel__sub">En este equipo, de cualquier fecha</p>
        {!sinEnviar || sinEnviar.cuantos === 0 ? (
          <p className="panel__empty">Todo está enviado.</p>
        ) : (
          <div className="unsent" role="group" aria-label="Sin enviar">
            <div className="unsent__n">{cuenta(sinEnviar.cuantos, country)}</div>
            <div className="unsent__meta">
              {sinEnviar.masViejo && (
                <div>
                  La más vieja: <b>{sinEnviar.masViejo.numero}</b>
                  {sinEnviar.masViejo.desde && `, ${haceCuanto(sinEnviar.masViejo.desde)}`}
                </div>
              )}
              {sinEnviar.conError > 0 && (
                <div className="unsent__error">
                  {cuenta(sinEnviar.conError, country)} con error al enviar
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default EstadoDeLosDocumentos;
